import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api';

/**
 * Cooldown raportów (PLAN §8a) — klient tylko POKAZUJE to, co egzekwuje serwer.
 *
 * Odliczanie w przeglądarce jest wygodą, a nie zabezpieczeniem: samo wyszarzenie przycisku
 * omija się odświeżeniem strony. Autorytetem jest `POST /reports/claim`, który przy zbyt
 * szybkiej próbie zwraca `429`.
 */
export const ClaimSchema = z.object({
  ok: z.boolean(),
  cooldownSeconds: z.number(),
  nextAllowedAt: z.number(),
});

const CooldownSchema = z.object({
  cooldownSeconds: z.number(),
  /** raport → ile SEKUND zostało; brak klucza = wolno teraz */
  remaining: z.record(z.string(), z.number()),
});

export type ReportKey =
  | 'transactions'
  | 'rationing'
  | 'employees'
  | 'items'
  | 'machines'
  | 'planogram'
  | 'assignments'
  | 'groups'
  | 'changes'
  | 'alerts'
  | 'accounts'
  | 'authoring'
  | 'itemGroups'
  | 'stanAutomatow';

/** Rzuca `ApiError` ze statusem 429, gdy jeszcze nie wolno — wołający pokazuje komunikat. */
export const claimReport = (report: ReportKey, groupId?: string | null) =>
  apiPost('/reports/claim', { report, ...(groupId ? { groupId } : {}) }, ClaimSchema);

export const fetchCooldowns = (groupId?: string | null) =>
  apiGet(`/reports/cooldown${groupId ? `?groupId=${groupId}` : ''}`, CooldownSchema);

/**
 * RAPORT STANU AUTOMATÓW — „co dowieźć".
 *
 * ⚠️ `pojemnosc` to suma pojemności PRZEGRÓDEK, a nie limit towaru w automacie. U klienta DBW
 * `MAXQTY`/`MINQTY`/`CRITICALLEVEL` są zerowe we wszystkich wierszach, więc drugie pojęcie
 * po prostu nie istnieje — szczegóły i pomiar w `api/src/reports/stan-automatu.ts`.
 */
const PozycjaSchema = z.object({
  itemId: z.string(),
  itemNumber: z.string(),
  itemName: z.string(),
  /**
   * SPS `Items.SUPPLIERPARTNUMBER` — „numer części dostawcy".
   *
   * ⚠️ `nullable().optional()`, bo instancja starsza od tej zmiany tego pola nie odda — a Zod
   * odrzuciłby wtedy CAŁY raport i ekran zostałby na „Ładowanie…" bez błędu w konsoli.
   */
  supplierPartNumber: z.string().nullable().optional(),
  unit: z.string().nullable(),
  pojemnosc: z.number(),
  stan: z.number(),
  brakuje: z.number(),
  przegrodek: z.number(),
  pustych: z.number(),
  miejsca: z.string(),
  progKrytyczny: z.number().nullable(),
  ponizejProgu: z.boolean().nullable(),
});

const AutomatSchema = z.object({
  machineId: z.string(),
  vmid: z.number(),
  name: z.string(),
  pozycje: z.array(PozycjaSchema),
  pojemnosc: z.number(),
  stan: z.number(),
  brakuje: z.number(),
  pozycjiNaZero: z.number(),
});

/**
 * Świeżość danych jedzie RAZEM z raportem.
 *
 * ⚠️ Na tych liczbach ktoś ładuje samochód i jedzie do klienta. Raport sprzed trzech dni wygląda
 * identycznie jak dzisiejszy, więc stan agenta jest CZĘŚCIĄ raportu, a nie osobnym ekranem, na
 * który trzeba pamiętać zajrzeć. `problem` niepusty znaczy, że agent się melduje, ale NIE dowozi
 * danych — obecność i praca to dwie różne rzeczy.
 */
const SwiezoscSchema = z.object({
  nodeId: z.string().nullable(),
  ostatniSygnal: z.string().nullable(),
  minutOdSygnalu: z.number().nullable(),
  problem: z.string().nullable(),
});

export const StanAutomatowSchema = z.object({
  automaty: z.array(AutomatSchema),
  podsumowanie: z.array(PozycjaSchema),
  pojemnosc: z.number(),
  stan: z.number(),
  brakuje: z.number(),
  pominietych: z.number(),
  swiezosc: SwiezoscSchema,
});

export type PozycjaStanu = z.infer<typeof PozycjaSchema>;
export type AutomatStanu = z.infer<typeof AutomatSchema>;
export type RaportStanu = z.infer<typeof StanAutomatowSchema>;

export const fetchStanAutomatow = (groupId: string, machineId?: string) =>
  apiGet(
    `/reports/stan-automatow?groupId=${encodeURIComponent(groupId)}` +
      (machineId ? `&machineId=${encodeURIComponent(machineId)}` : ''),
    StanAutomatowSchema,
  );
